import { defineComponent as _defineComponent } from 'vue'
import { withModifiers as _withModifiers, withKeys as _withKeys, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, unref as _unref, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["value", "disabled", "aria-expanded", "aria-controls", "aria-activedescendant", "onKeydown"]
const _hoisted_2 = {
  key: 0,
  class: "col-span-2 row-start-2 overflow-hidden rounded-lg border border-indigo-200 bg-white shadow-lg"
}
const _hoisted_3 = {
  key: 0,
  class: "p-3 text-sm text-gray-600",
  role: "status"
}
const _hoisted_4 = {
  key: 1,
  class: "p-3 text-sm text-gray-600",
  role: "status"
}
const _hoisted_5 = ["id", "aria-selected", "aria-label", "onClick"]
const _hoisted_6 = { class: "flex h-16 w-14 shrink-0 items-center justify-center rounded bg-gray-50" }
const _hoisted_7 = ["src", "alt", "onError"]
const _hoisted_8 = {
  key: 1,
  class: "px-1 text-center text-xs text-gray-500"
}
const _hoisted_9 = { class: "min-w-0 text-sm" }
const _hoisted_10 = { class: "block break-words font-semibold" }
const _hoisted_11 = { class: "block break-words text-gray-600" }
const _hoisted_12 = { class: "block font-medium text-indigo-700" }
const _hoisted_13 = { class: "block text-xs text-gray-600" }
const _hoisted_14 = {
  key: 3,
  class: "border-t bg-gray-50 px-3 py-2 text-xs text-gray-600"
}

import { computed, getCurrentInstance, ref, watch } from 'vue';
import { replacementMoney, replacementProductTotal } from '@/api/remplacements';
import type { ReplacementProduct, ReplacementSelection } from '@/api/remplacements';


export default /*@__PURE__*/_defineComponent({
  __name: 'ReplacementReferenceSearch',
  props: {
    modelValue: {},
    products: {},
    selection: {},
    loading: { type: Boolean },
    disabled: { type: Boolean }
  },
  emits: ["update:modelValue", "choose", "search"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const root = ref<HTMLElement>();
const expanded = ref(false);
const active = ref(-1);
const failedImages = ref(new Set<string>());
const listId = `replacement-reference-${getCurrentInstance()?.uid}`;
const matches = computed(() => props.products.slice(0, 5));
const showResults = computed(() => expanded.value && Boolean(props.modelValue.trim()) && !props.disabled);
function totalLabel(item: ReplacementProduct) {
  const total = replacementProductTotal(item, props.selection).amount;
  return total != null ? `${replacementMoney(total)} HT au total` : 'Quantité à préciser';
}
function typeReference(event: Event) {
  expanded.value = true;
  emit('update:modelValue', (event.target as HTMLInputElement).value);
}
function select(id: number) {
  emit('choose', id);
  expanded.value = false;
  active.value = -1;
}
function move(direction: number) {
  expanded.value = true;
  if (props.loading || !matches.value.length) return;
  active.value = direction > 0 ? (active.value + 1) % matches.value.length : active.value < 0 ? matches.value.length - 1 : (active.value - 1 + matches.value.length) % matches.value.length;
}
function enter() {
  const item = matches.value[active.value];
  if (showResults.value && !props.loading && item) select(item.id_produit);
  else emit('search');
}
function leave(event: FocusEvent) {
  if (!root.value?.contains(event.relatedTarget as Node | null)) expanded.value = false;
}
watch(() => [props.modelValue, props.products], () => { active.value = -1; });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "root",
    ref: root,
    class: "contents",
    onFocusout: leave,
    onKeydown: _cache[4] || (_cache[4] = _withKeys(_withModifiers(($event: any) => (expanded.value = false), ["stop","prevent"]), ["esc"]))
  }, [
    _createElementVNode("input", {
      value: _ctx.modelValue,
      disabled: _ctx.disabled,
      type: "text",
      role: "combobox",
      "aria-label": "Rechercher une référence",
      "aria-autocomplete": "list",
      "aria-expanded": showResults.value,
      "aria-controls": showResults.value && !_ctx.loading && matches.value.length ? listId : undefined,
      "aria-activedescendant": active.value >= 0 && showResults.value ? `${listId}-${active.value}` : undefined,
      autocomplete: "off",
      placeholder: "Rechercher une référence…",
      class: "col-start-1 row-start-1 w-full min-w-0 rounded border px-3 py-2",
      maxlength: "100",
      onInput: typeReference,
      onFocus: _cache[0] || (_cache[0] = ($event: any) => (expanded.value = true)),
      onKeydown: [
        _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (move(1)), ["prevent"]), ["down"])),
        _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (move(-1)), ["prevent"]), ["up"])),
        _withKeys(_withModifiers(enter, ["prevent"]), ["enter"])
      ]
    }, null, 40, _hoisted_1),
    (showResults.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (_ctx.loading)
            ? (_openBlock(), _createElementBlock("p", _hoisted_3, "Recherche des articles…"))
            : (!matches.value.length)
              ? (_openBlock(), _createElementBlock("p", _hoisted_4, "Aucun article trouvé. Essayez une autre référence."))
              : (_openBlock(), _createElementBlock("div", {
                  key: 2,
                  id: listId,
                  role: "listbox",
                  "aria-label": "Résultats des références",
                  class: "divide-y divide-gray-100"
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(matches.value, (item, index) => {
                    return (_openBlock(), _createElementBlock("button", {
                      id: `${listId}-${index}`,
                      key: item.id_produit,
                      type: "button",
                      role: "option",
                      "aria-selected": active.value === index,
                      "aria-label": `Sélectionner ${item.reference} · ${item.couleur} · ${totalLabel(item)}`,
                      class: _normalizeClass(['flex w-full items-center gap-3 p-3 text-left hover:bg-indigo-50 focus:bg-indigo-50', active.value === index ? 'bg-indigo-50' : 'bg-white']),
                      onMousedown: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["prevent"])),
                      onClick: ($event: any) => (select(item.id_produit))
                    }, [
                      _createElementVNode("span", _hoisted_6, [
                        (item.image_url && !failedImages.value.has(item.image_url))
                          ? (_openBlock(), _createElementBlock("img", {
                              key: 0,
                              src: item.image_url,
                              alt: item.reference,
                              class: "h-full w-full object-contain",
                              referrerpolicy: "no-referrer",
                              onError: ($event: any) => (failedImages.value.add(item.image_url))
                            }, null, 40, _hoisted_7))
                          : (_openBlock(), _createElementBlock("span", _hoisted_8, "Sans photo"))
                      ]),
                      _createElementVNode("span", _hoisted_9, [
                        _createElementVNode("span", _hoisted_10, _toDisplayString(item.reference), 1),
                        _createElementVNode("span", _hoisted_11, _toDisplayString(item.couleur || 'Couleur non renseignée'), 1),
                        _createElementVNode("span", _hoisted_12, _toDisplayString(totalLabel(item)), 1),
                        _createElementVNode("span", _hoisted_13, _toDisplayString(_unref(replacementProductTotal)(item, _ctx.selection).description), 1)
                      ])
                    ], 42, _hoisted_5))
                  }), 128))
                ])),
          (!_ctx.loading && _ctx.products.length > matches.value.length)
            ? (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(matches.value.length) + " premiers résultats sur " + _toDisplayString(_ctx.products.length) + ". Précisez la référence ou consultez les articles ci-dessous.", 1))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true)
  ], 544))
}
}

})